import type {
  Feature,
  Geometry,
  LineString,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon,
} from "geojson";

/**
 * Check if two coordinates are the same
 * @param coordA - The first coordinate
 * @param coordB - The second coordinate
 * @returns True if the coordinates are the same
 */
export function isSameCoord(coordA: number[], coordB: number[]): boolean {
  return coordA[0] === coordB[0] && coordA[1] === coordB[1];
}

/**
 * Type guard to check if geometry has coordinates property
 * @param geometry - The geometry to check
 * @returns True if the geometry has coordinates property
 */
export function hasCoordinates(
  geometry: Geometry | null,
): geometry is
  | Point
  | LineString
  | Polygon
  | MultiPoint
  | MultiLineString
  | MultiPolygon {
  return !!geometry && "coordinates" in geometry;
}

/**
 * Extract feature ID from either feature.id or feature.properties.id
 * @param feature - The GeoJSON feature
 * @returns The feature ID or undefined if not found
 */
export function getFeatureId(feature: Feature): string {
  const id = feature.id ?? feature.properties?.id;
  if (!id) {
    throw new Error("Feature must have a valid id.");
  }
  return id;
}

export function isGeometryValid(feature: Feature): boolean {
  try {
    validateGeometry(feature);
    return true;
  } catch (_) {
    return false;
  }
}

const validatePolygon = (feature: Feature<Polygon>): void => {
  if (
    !feature.geometry.coordinates ||
    feature.geometry.coordinates.length < 1
  ) {
    throw new Error("Polygon geometry cannot have fewer than 1 ring");
  }
  for (const ring of feature.geometry.coordinates) {
    if (ring.length < 4) {
      throw new Error("Polygon geometry cannot have fewer than 3 points");
    }
    if (!isSameCoord(ring[0], ring[ring.length - 1])) {
      throw new Error("Polygon geometry must be closed");
    }
  }
};

/**
 * Validates if a geometry is valid according to GeoJSON specifications
 * @param geometry - The geometry to validate
 * @throws Error if the geometry is invalid
 */
export function validateGeometry(feature: Feature): void {
  switch (feature.geometry.type) {
    case "Point":
      if (
        !feature.geometry.coordinates ||
        feature.geometry.coordinates.length < 2
      ) {
        throw new Error("Point geometry cannot have fewer than 2 coordinates");
      }
      break;

    case "MultiPoint":
      if (
        !feature.geometry.coordinates ||
        feature.geometry.coordinates.length < 1
      ) {
        throw new Error(
          "MultiPoint geometry cannot have fewer than 1 coordinate",
        );
      }
      break;

    case "LineString":
      if (
        !feature.geometry.coordinates ||
        feature.geometry.coordinates.length < 2
      ) {
        throw new Error("LineString geometry cannot have fewer than 2 points");
      }
      break;

    case "MultiLineString":
      if (
        !feature.geometry.coordinates ||
        feature.geometry.coordinates.length < 1
      ) {
        throw new Error(
          "MultiLineString geometry cannot have fewer than 1 line",
        );
      }
      for (const line of feature.geometry.coordinates) {
        if (line.length < 2) {
          throw new Error(
            "Each line in MultiLineString cannot have fewer than 2 points",
          );
        }
      }
      break;

    case "Polygon":
      validatePolygon(feature as Feature<Polygon>);
      break;

    case "MultiPolygon":
      if (
        !feature.geometry.coordinates ||
        feature.geometry.coordinates.length < 1
      ) {
        throw new Error(
          "MultiPolygon geometry cannot have fewer than 1 polygon",
        );
      }
      for (const polygon of feature.geometry.coordinates) {
        for (const ring of polygon) {
          if (ring.length < 4) {
            throw new Error("Polygon geometry cannot have fewer than 3 points");
          }
        }
      }
      break;

    case "GeometryCollection":
      throw new Error("GeometryCollection validation is not supported");

    default:
      throw new Error(
        `Unsupported geometry type: ${(feature.geometry as any).type}`,
      );
  }
}
